import { computed, makeObservable } from 'mobx'
import type moment from 'moment'
import type { $Keys } from 'utility-types'

import { stringToDate } from 'modules/core/lib/dates'
import { ModelWithPolicy } from 'modules/core/lib/modelWithPolicy'
import AttendancePeriodPolicy, {
  AttendancePeriodVerbTypes,
} from 'modules/core/store/models/policies/AttendancePeriodPolicy'

const STATUS = {
  pending: 'pending',
  approved: 'approved',
  in_progress: 'in_progress',
}

const TIME_UNIT = {
  minute: 'minute',
  half_day: 'half_day',
  none: 'none',
}

export type Status = $Keys<typeof STATUS>

export type TimeUnit = $Keys<typeof TIME_UNIT>

interface IPeriod {
  [key: string]: any
}

class Period extends ModelWithPolicy<IPeriod, AttendancePeriodVerbTypes> {
  static urlRoot() {
    return '/attendance/periods'
  }

  static STATUS = STATUS
  static TIME_UNIT = TIME_UNIT

  constructor(attr: IPeriod = {}) {
    super(attr)
    makeObservable(this, {
      baselineMinutes: computed,
      state: computed,
      startOn: computed,
      endOn: computed,
      notInProgress: computed,
      isApproved: computed,
      isApprovable: computed,
      isPlannedOvertime: computed,
    })
  }

  get policy() {
    return new AttendancePeriodPolicy(this)
  }

  estimationPerDay(index: number): number {
    return Number(this.attributes.estimated_minutes_distribution[index - 1])
  }

  isEstimationByShifts(): boolean {
    if (!this.has('estimated_by_shifts_distribution')) return false

    return this.get('estimated_by_shifts_distribution').some((value: boolean) => value)
  }

  isTotalBreakTaken(index: number): boolean {
    if (this.attributes.total_break_taken[index] !== undefined) {
      return this.attributes.total_break_taken[index]
    } else return true
  }

  get baselineMinutes(): number {
    return Math.max(...this.attributes.distribution)
  }

  get state(): Status {
    return this.attributes.state
  }

  get startOn(): moment.Moment {
    return stringToDate(this.attributes.start_on, 'backend')
  }

  get endOn(): moment.Moment {
    return stringToDate(this.attributes.end_on, 'backend')
  }

  get notInProgress(): boolean {
    return this.state !== STATUS.in_progress
  }

  get isApproved(): boolean {
    return this.state === STATUS.approved
  }

  get isApprovable(): boolean {
    return this.notInProgress && this.policy.can('approvePeriod')
  }

  get timeUnit(): TimeUnit {
    const timeUnit = this.attributes.time_units_distibution.find(
      (unit: unknown) => unit !== TIME_UNIT.none
    )

    return timeUnit || TIME_UNIT.none
  }

  get isDaily(): boolean {
    return this.timeUnit === TIME_UNIT.half_day
  }

  get isPlannedOvertime(): boolean {
    return !!this.attributes.estimated_overtime_minutes
  }

  timeUnitByDay(dayIndex: number): TimeUnit {
    const index = dayIndex - 1

    return this.attributes.time_units_distibution[index] || TIME_UNIT.none
  }

  dateIsApproved(date: string): boolean {
    return this.attributes.reviews.includes(date)
  }
}

export class CustomPeriod extends Period {
  static urlRoot() {
    return '/attendance/custom_periods'
  }
}

export default Period
